import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Phone, PhoneOff, Mic, X, Loader2 } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { useProductName, isPlatformHost } from "@/context/BrandContext";

const CALL_COUNT_KEY = "vicibots_widget_call_count";

function readCallCount() {
  try {
    const n = Number.parseInt(localStorage.getItem(CALL_COUNT_KEY) || "0", 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function bumpCallCount() {
  const next = readCallCount() + 1;
  try { localStorage.setItem(CALL_COUNT_KEY, String(next)); } catch { /* noop */ }
  return next;
}

/**
 * ViciBots web-phone on marketing pages.
 * Config from GET /settings/public (enable, campaign prefix, optional label).
 * variant="header" → inline in marketing header; "floating" → fixed corner FAB.
 * Click → panel → Live WebRTC via /test-call/widget/*.
 */
export default function ViciBotsWidget({ variant = "header" }) {
  const productName = useProductName();
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | starting | live | done | error
  const [error, setError] = useState("");
  const [listening, setListening] = useState(false);
  const [duplex, setDuplex] = useState(false);
  const [campaignName, setCampaignName] = useState("");
  const [lines, setLines] = useState([]);
  const [limitReached, setLimitReached] = useState(false);

  const wsRef = useRef(null);
  const mediaRef = useRef(null);
  const audioElRef = useRef(null);
  const pcmCtxRef = useRef(null);
  const pcmNextRef = useRef(0);
  const pcmSourcesRef = useRef([]);
  const sessionRef = useRef(null);
  const chatScrollRef = useRef(null);
  const linesRef = useRef([]);
  const campaignNameRef = useRef("");
  const endedHandledRef = useRef(false);

  useEffect(() => { linesRef.current = lines; }, [lines]);
  useEffect(() => { campaignNameRef.current = campaignName; }, [campaignName]);

  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [lines, status, error, open]);

  useEffect(() => {
    let cancelled = false;
    api.get("/settings/public")
      .then(({ data }) => {
        if (cancelled) return;
        if (!data?.vicibots_widget_enabled || !(data?.vicibots_widget_campaign_prefix || "").trim()) {
          setCfg(null);
          return;
        }
        const hideReseller = data.vicibots_widget_hide_on_reseller !== false;
        if (hideReseller && !isPlatformHost()) {
          setCfg(null);
          return;
        }
        const callLimit = Number.parseInt(data.vicibots_widget_call_limit ?? 0, 10) || 0;
        const used = readCallCount();
        if (callLimit > 0 && used >= callLimit) {
          setLimitReached(true);
          setCfg(null);
          return;
        }
        setLimitReached(false);
        setCfg({
          enabled: true,
          position: data.vicibots_widget_position === "left" ? "left" : "right",
          prefix: data.vicibots_widget_campaign_prefix.trim(),
          text: (data.vicibots_widget_text || "").trim(),
          callLimit,
        });
      })
      .catch(() => { if (!cancelled) setCfg(null); });
    return () => { cancelled = true; };
  }, []);

  const clearPcm = useCallback(() => {
    pcmSourcesRef.current.forEach((s) => { try { s.stop(0); } catch { /* noop */ } });
    pcmSourcesRef.current = [];
    pcmNextRef.current = 0;
    try { pcmCtxRef.current?.close(); } catch { /* noop */ }
    pcmCtxRef.current = null;
  }, []);

  const stopMic = useCallback(() => {
    try { mediaRef.current?.stop?.(); } catch { /* noop */ }
    mediaRef.current = null;
  }, []);

  const cleanup = useCallback(() => {
    try { wsRef.current?.send(JSON.stringify({ type: "end" })); } catch { /* noop */ }
    try { wsRef.current?.close(); } catch { /* noop */ }
    wsRef.current = null;
    stopMic();
    clearPcm();
    sessionRef.current = null;
  }, [clearPcm, stopMic]);

  useEffect(() => () => cleanup(), [cleanup]);

  const ensurePcmCtx = async () => {
    if (!pcmCtxRef.current || pcmCtxRef.current.state === "closed") {
      pcmCtxRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
      pcmNextRef.current = 0;
    }
    if (pcmCtxRef.current.state === "suspended") {
      try { await pcmCtxRef.current.resume(); } catch { /* noop */ }
    }
    return pcmCtxRef.current;
  };

  const playPcmWav = async (wavB64) => {
    try {
      const ctx = await ensurePcmCtx();
      const bin = atob(wavB64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
      const decoded = await ctx.decodeAudioData(bytes.buffer.slice(0));
      const src = ctx.createBufferSource();
      src.buffer = decoded;
      src.connect(ctx.destination);
      const now = ctx.currentTime;
      const startAt = Math.max(now + 0.01, pcmNextRef.current || now);
      src.start(startAt);
      pcmNextRef.current = startAt + decoded.duration;
      pcmSourcesRef.current.push(src);
      src.onended = () => {
        pcmSourcesRef.current = pcmSourcesRef.current.filter((x) => x !== src);
      };
    } catch {
      const el = audioElRef.current;
      if (el) {
        el.src = `data:audio/wav;base64,${wavB64}`;
        el.play().catch(() => {});
      }
    }
  };

  const downsample = (input, inRate, outRate = 16000) => {
    if (inRate === outRate) return input;
    const ratio = inRate / outRate;
    const outLen = Math.floor(input.length / ratio);
    const out = new Float32Array(outLen);
    for (let i = 0; i < outLen; i += 1) out[i] = input[Math.floor(i * ratio)] || 0;
    return out;
  };

  const playNode = (m, ws) => {
    let acked = false;
    const done = () => {
      if (acked) return;
      acked = true;
      try { ws?.send(JSON.stringify({ type: "played", node_id: m.node_id })); } catch { /* noop */ }
    };
    const el = audioElRef.current;
    const sess = sessionRef.current;
    let src = null;
    if (m.mode === "wav" && m.wav_b64) {
      src = `data:audio/wav;base64,${m.wav_b64}`;
    } else if (m.mode === "audio" && m.audio_file_id && sess?.session_id && sess?.token) {
      // Public widget cannot use JWT /audio — use session-scoped widget audio URL.
      src = `${API}/test-call/widget/audio/${encodeURIComponent(m.audio_file_id)}`
        + `?session_id=${encodeURIComponent(sess.session_id)}`
        + `&token=${encodeURIComponent(sess.token)}`;
    }
    if (src && el) {
      el.onended = done;
      el.onerror = done;
      // Safety: ack after duration even if onended never fires (some WAVs).
      const fallbackMs = Math.min(((Number(m.duration_s) || 8) * 1000) + 1500, 90000);
      const fallbackTimer = window.setTimeout(done, fallbackMs);
      el.onloadedmetadata = () => {
        if (Number.isFinite(el.duration) && el.duration > 0) {
          window.clearTimeout(fallbackTimer);
          window.setTimeout(done, Math.min(el.duration * 1000 + 800, 90000));
        }
      };
      el.src = src;
      el.play().catch(done);
      setLines((prev) => {
        const raw = (m.prompt_text || m.label || "").trim();
        const fn = (m.filename || "").trim();
        const looksLikeFile = /\.(wav|mp3|ogg|m4a|webm|aac)$/i.test(raw)
          || /\.(wav|mp3|ogg|m4a|webm|aac)$/i.test(fn)
          || /^tts-/i.test(raw);
        const label = (!looksLikeFile && raw)
          ? raw
          : (!/\.(wav|mp3|ogg|m4a|webm|aac)$/i.test(fn) && fn && !/^tts-/i.test(fn))
            ? fn
            : "Playing audio…";
        return [...prev.slice(-40), { role: "bot", text: label }];
      });
      return;
    }
    if (m.prompt_text && window.speechSynthesis) {
      try {
        window.speechSynthesis.cancel();
        const utter = new SpeechSynthesisUtterance(m.prompt_text);
        utter.onend = done;
        utter.onerror = done;
        window.speechSynthesis.speak(utter);
        return;
      } catch { /* fall through */ }
    }
    // No playable media — still wait a beat so the flow doesn't race to Hang Up.
    window.setTimeout(done, Math.min(((Number(m.duration_s) || 1) * 1000), 5000));
  };

  const finishCall = useCallback((nextStatus = "done") => {
    if (endedHandledRef.current) {
      cleanup();
      setListening(false);
      setDuplex(false);
      setStatus(nextStatus);
      return;
    }
    endedHandledRef.current = true;
    const sess = sessionRef.current;
    const chat = (linesRef.current || [])
      .filter((l) => (l?.text || "").trim())
      .map((l) => ({ role: l.role === "bot" ? "bot" : "you", text: String(l.text || "").slice(0, 2000) }));
    const camp = campaignNameRef.current || "";
    cleanup();
    setListening(false);
    setDuplex(false);
    setStatus(nextStatus);

    // Count a completed attempt toward the visitor call limit.
    const used = bumpCallCount();
    const lim = cfg?.callLimit || 0;
    if (lim > 0 && used >= lim) {
      setLimitReached(true);
      setCfg(null);
    }

    if (sess?.session_id && sess?.token) {
      api.post("/test-call/widget/transcript", {
        session_id: sess.session_id,
        token: sess.token,
        lines: chat,
        campaign_name: camp,
      }).catch(() => { /* email is best-effort */ });
    }
  }, [cleanup, cfg?.callLimit]);

  const hangup = () => {
    finishCall("done");
  };

  const startCall = async () => {
    if (status === "starting" || status === "live") return;
    if (limitReached) return;
    setError("");
    setLines([]);
    endedHandledRef.current = false;
    setStatus("starting");
    setListening(false);
    setDuplex(false);
    clearPcm();
    try {
      const el = audioElRef.current;
      if (el) {
        el.src = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";
        await el.play();
      }
      await ensurePcmCtx();
    } catch { /* autoplay unlock best-effort */ }

    try {
      const { data } = await api.post("/test-call/widget/start");
      sessionRef.current = data;
      setCampaignName(data.campaign_name || "");
      const wsUrl = `${API.replace(/^http/, "ws")}/test-call/widget/ws/${data.session_id}?token=${encodeURIComponent(data.token)}`;
      const ws = new WebSocket(wsUrl);
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;

      ws.onopen = () => setStatus("live");
      ws.onerror = () => {
        setError("Connection failed");
        setStatus("error");
      };
      ws.onclose = () => {
        stopMic();
        if (!endedHandledRef.current) {
          finishCall("done");
        } else {
          setListening(false);
          setDuplex(false);
          setStatus((s) => (s === "live" || s === "starting" ? "done" : s));
        }
      };
      ws.onmessage = (ev) => {
        let m;
        try { m = JSON.parse(ev.data); } catch { return; }
        if (m.type === "listening") { setListening(true); return; }
        if (m.type === "live_start") { setDuplex(true); setListening(false); return; }
        if (m.type === "live_end") { setDuplex(false); return; }
        if (m.type === "pcm_clear") { clearPcm(); return; }
        if (m.type === "pcm_wav" && m.wav_b64) {
          setListening(false);
          playPcmWav(m.wav_b64);
          return;
        }
        if (m.type === "stt" && m.transcript) {
          let text = String(m.transcript || "").trim();
          if (!text) return;
          let role = m.role === "bot" ? "bot" : "you";
          // Legacy prefix from older backend dumps
          if (/^\[bot\]\s*/i.test(text)) {
            role = "bot";
            text = text.replace(/^\[bot\]\s*/i, "").trim();
          }
          if (!text) return;
          setLines((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            // Same speaker → one bubble. Gemini Live sends short fragments
            // ("Can I" / "have your" / "name,") that must merge into one message.
            if (last && last.role === role) {
              let merged;
              if (text.startsWith(last.text)) {
                merged = text; // cumulative update
              } else if (last.text.startsWith(text)) {
                merged = last.text; // shorter duplicate
              } else if (last.text.endsWith(text)) {
                merged = last.text; // already appended
              } else {
                const needsSpace = !/[\s([{/-]$/.test(last.text) && !/^[\s.,!?;:)\]}]/.test(text);
                merged = `${last.text}${needsSpace ? " " : ""}${text}`;
              }
              next[next.length - 1] = { role, text: merged.replace(/\s+/g, " ").trim() };
              return next.slice(-48);
            }
            next.push({ role, text });
            return next.slice(-48);
          });
          return;
        }
        if (m.type === "play") {
          playNode(m, ws);
          return;
        }
        if (m.type === "done") {
          setLiveFlagsOff();
          stopMic();
          if (!endedHandledRef.current) finishCall("done");
          else setStatus("done");
          return;
        }
        if (m.type === "error") {
          setError(formatApiError(m.detail) || "Call error");
          setStatus("error");
          setLiveFlagsOff();
          stopMic();
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      try { await ctx.resume(); } catch { /* noop */ }
      const source = ctx.createMediaStreamSource(stream);
      const proc = ctx.createScriptProcessor(4096, 1, 1);
      proc.onaudioprocess = (e) => {
        if (ws.readyState !== 1) return;
        const frame = downsample(e.inputBuffer.getChannelData(0), ctx.sampleRate, 16000);
        ws.send(frame.buffer);
      };
      const sink = ctx.createGain();
      sink.gain.value = 0;
      source.connect(proc); proc.connect(sink); sink.connect(ctx.destination);
      let stopped = false;
      mediaRef.current = {
        stop: () => {
          if (stopped) return;
          stopped = true;
          try { proc.disconnect(); source.disconnect(); sink.disconnect(); } catch { /* noop */ }
          try { stream.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
          try { if (ctx.state !== "closed") ctx.close(); } catch { /* noop */ }
        },
      };
    } catch (e) {
      const msg = e?.response
        ? formatApiError(e.response?.data?.detail)
        : (e?.message?.includes("Permission") || e?.name === "NotAllowedError"
          ? "Microphone permission denied"
          : formatApiError(e?.message) || "Could not start call");
      setError(msg);
      setStatus("error");
      cleanup();
    }
  };

  const setLiveFlagsOff = () => {
    setListening(false);
    setDuplex(false);
  };

  const openPanel = () => {
    setOpen(true);
    if (status === "idle" || status === "done" || status === "error") {
      startCall();
    }
  };

  const closePanel = () => {
    if (status === "live" || status === "starting") {
      hangup();
    } else if (!endedHandledRef.current && sessionRef.current) {
      finishCall("done");
    }
    setOpen(false);
    setStatus("idle");
    setError("");
    setLines([]);
  };

  if (!cfg?.enabled || limitReached) return null;

  const isHeader = variant === "header";
  const side = cfg.position === "left" ? "left-5 sm:left-6" : "right-5 sm:right-6";
  const label = productName || "ViciBots";
  const widgetText = (cfg.text || "").trim();
  const hasText = Boolean(widgetText);

  // Portal header panel to <body> — header backdrop-blur makes position:fixed
  // resolve against the header, which clipped the panel off the right on desktop.
  const panelClass = isHeader
    ? "fixed left-3 right-3 top-[4.25rem] z-[200] mx-auto w-auto max-w-[22rem] overflow-hidden rounded-2xl border border-border bg-card shadow-2xl sm:left-auto sm:right-4 sm:mx-0 sm:w-[min(22rem,calc(100vw-2rem))]"
    : `absolute bottom-[4.5rem] ${
        cfg.position === "left" ? "left-0 origin-bottom-left" : "right-0 origin-bottom-right"
      } w-[min(100vw-2.5rem,22rem)] overflow-hidden rounded-2xl border border-border bg-card shadow-2xl`;

  // Header: quiet outline pill so it doesn't compete with Get started.
  // Floating: solid primary FAB.
  const fabClass = isHeader
    ? (hasText
      ? "group relative inline-flex h-8 max-w-[12rem] items-center gap-1.5 rounded-full border border-border bg-background pl-1.5 pr-3 text-foreground transition-colors hover:bg-muted"
      : "group relative grid h-8 w-8 place-items-center rounded-full border border-border bg-background text-foreground transition-colors hover:bg-muted")
    : (hasText
      ? "group relative inline-flex h-14 max-w-[min(100vw-2.5rem,18rem)] items-center gap-2 rounded-full bg-primary pl-2 pr-5 text-primary-foreground shadow-lg shadow-primary/30 transition-transform hover:scale-[1.03]"
      : "group relative grid h-14 w-14 place-items-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 transition-transform hover:scale-105");

  const iconWrapClass = hasText
    ? (isHeader
      ? "relative grid h-5 w-5 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground"
      : "relative grid h-10 w-10 shrink-0 place-items-center rounded-full bg-white/15")
    : (isHeader
      ? "relative grid h-5 w-5 place-items-center rounded-full bg-primary text-primary-foreground"
      : "relative");

  const iconSize = isHeader
    ? (hasText ? "h-3 w-3" : "h-3 w-3")
    : (hasText ? "h-5 w-5" : "h-6 w-6");

  const panel = open ? (
    <div className={panelClass} data-testid="vicibots-widget-panel">
      <div className="flex items-center justify-between gap-2 border-b border-border bg-primary px-4 py-3 text-primary-foreground">
        <div className="min-w-0">
          <p className="disp text-sm font-bold truncate">VICIBots AI Agent</p>
          <p className="text-[11px] opacity-80 truncate">
            {campaignName || `Prefix ${cfg.prefix}`}
          </p>
        </div>
        <button
          type="button"
          onClick={closePanel}
          className="grid h-8 w-8 place-items-center rounded-lg hover:bg-white/15"
          aria-label="Close"
          data-testid="vicibots-widget-close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div
        ref={chatScrollRef}
        className="flex h-48 flex-col gap-2 overflow-y-auto bg-muted/30 p-3 text-sm"
        data-testid="vicibots-widget-chat"
      >
        {status === "starting" && (
          <p className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Connecting…
          </p>
        )}
        {error && <p className="rounded-lg bg-destructive/10 px-3 py-2 text-destructive text-xs">{error}</p>}
        {!error && lines.length === 0 && status === "live" && (
          <p className="text-muted-foreground text-xs">Call connected — speak when you hear the agent.</p>
        )}
        {lines.map((l, i) => (
          <div
            key={`${i}-${l.role}-${l.text.slice(0, 24)}`}
            className={`max-w-[90%] rounded-2xl px-3 py-2 text-xs leading-relaxed ${
              l.role === "you"
                ? "ml-auto bg-primary text-primary-foreground"
                : "bg-background border border-border"
            }`}
          >
            {l.text}
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Mic className={`h-3.5 w-3.5 ${listening || duplex ? "text-primary" : ""}`} />
          {status === "live"
            ? (duplex ? "Live conversation" : listening ? "Listening…" : "Agent speaking")
            : status === "done"
              ? "Call ended"
              : status === "error"
                ? "Unavailable"
                : "Ready"}
        </div>
        <div className="flex gap-2">
          {(status === "done" || status === "error" || status === "idle") && (
            <button
              type="button"
              onClick={startCall}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground"
              data-testid="vicibots-widget-recall"
            >
              <Phone className="h-3.5 w-3.5" /> Call again
            </button>
          )}
          {(status === "live" || status === "starting") && (
            <button
              type="button"
              onClick={hangup}
              className="inline-flex items-center gap-1.5 rounded-lg bg-destructive px-3 py-2 text-xs font-semibold text-destructive-foreground"
              data-testid="vicibots-widget-hangup"
            >
              <PhoneOff className="h-3.5 w-3.5" /> Hang up
            </button>
          )}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div
      className={isHeader ? "relative shrink-0" : `fixed bottom-5 z-[60] ${side} sm:bottom-6`}
      data-testid="vicibots-widget"
      data-variant={isHeader ? "header" : "floating"}
      data-position={cfg.position}
    >
      {isHeader && (
        <style>{`
          @keyframes vicibots-phone-ring {
            0% { transform: scale(0.85); opacity: 0.85; }
            70% { transform: scale(1.55); opacity: 0; }
            100% { transform: scale(1.55); opacity: 0; }
          }
          .vicibots-phone-ring {
            animation: vicibots-phone-ring 1.8s ease-out infinite;
          }
        `}</style>
      )}
      <audio ref={audioElRef} className="hidden" playsInline />

      {isHeader
        ? (typeof document !== "undefined" && panel ? createPortal(panel, document.body) : null)
        : panel}

      <button
        type="button"
        onClick={() => (open ? closePanel() : openPanel())}
        className={fabClass}
        aria-label={open ? "Close VICIBots AI Agent" : (hasText ? widgetText : `Call ${label}`)}
        data-testid="vicibots-widget-fab"
        data-has-text={hasText ? "true" : "false"}
      >
        {!isHeader && (
          <span
            className={`pointer-events-none absolute ${hasText ? "left-2 h-10 w-10" : "inset-0"} animate-ping rounded-full bg-primary/40 opacity-40`}
            aria-hidden
          />
        )}
        <span className={iconWrapClass}>
          {isHeader && !open && (
            <>
              <span
                className="pointer-events-none absolute inset-0 animate-ping rounded-full bg-primary/45"
                aria-hidden
              />
              <span
                className="vicibots-phone-ring pointer-events-none absolute -inset-1 rounded-full border border-primary/50"
                aria-hidden
              />
            </>
          )}
          {open
            ? <X className={`relative ${iconSize}`} />
            : <Phone className={`relative ${iconSize}`} />}
        </span>
        {hasText && (
          <span
            className={`relative truncate tracking-tight ${
              isHeader ? "text-xs font-medium" : "text-sm font-semibold"
            }`}
          >
            {widgetText}
          </span>
        )}
      </button>
    </div>
  );
}
